import React, { useEffect, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import API_BASE_URL from '../../../../Constants';
import { ColorRing } from 'react-loader-spinner';

import ViewTaxDetailsModal from './ViewTaxDetailsModal';
import ListHeader from '../../../../components/ListHeader';
import AddTaxDetailsModal from './AddTaxDetailsModal';

const TaxFields = [
    { key: 'actions', label: 'Actions' },
    { key: 'id', label: 'Tax ID' },
    { key: 'description', label: 'Description' },
    {
        key: 'type',
        label: 'Type',
        options: [
            { value: 1, label: 'Sales' },
            { value: 2, label: 'Purchase' },
        ],
        map: { 1: 'Sales', 2: 'Purchase' }
    },
    { key: 'percent', label: 'Tax Percent', type: 'number', min: 0, max: 100 },
    { key: 'addressOne', label: 'Address Line 1', type: 'text' },
    { key: 'addressTwo', label: 'Address Line 2', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'zip', label: 'Zip Code', type: 'number' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'fax', label: 'Fax', type: 'text' },
];

const TaxDetailsList = () => {
    const [taxDetails, setTaxDetails] = useState([]);
    const [filteredTaxDetails, setFilteredTaxDetails] = useState([]);
    const [selectedTaxDetails, setSelectedTaxDetails] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');




    const [viewMode, setViewMode] = useState('table');

    useEffect(() => {
        setFilteredTaxDetails(taxDetails);
    }, [taxDetails]);

    const fetchTaxDetails = async (page = currentPage, size = rowsPerPage, query = searchQuery) => {
        try {
            setIsLoading(true);
            const queryString = query ? `&q=${encodeURIComponent(query)}` : '';
            const response = await fetch(`${API_BASE_URL}/api/setup/taxdetails/filtered-taxdetails?page=${page - 1}&size=${size}${queryString}`, { credentials: 'include' });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setTaxDetails(Array.isArray(data.content) ? data.content : []);
            setCurrentPage(data.number + 1);
            setTotalPages(data.totalPages);
            setIsLoading(false);
        } catch (error) {
            console.error('Fetch error: ', error);
            setTaxDetails([]);

        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchTaxDetails(currentPage, rowsPerPage, searchQuery);
    }, [currentPage, rowsPerPage]);


    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };


    const handleSearch = () => fetchTaxDetails(1, rowsPerPage, searchQuery);



    const handleOpenViewModal = (tax) => {
        setSelectedTaxDetails(tax);
        setIsViewOpen(true);
    };

    const setNewComponentLeft = () => {
        const currentIndex = filteredTaxDetails.indexOf(selectedTaxDetails) || 0;
        setSelectedTaxDetails(filteredTaxDetails[(currentIndex - 1 + filteredTaxDetails.length) % filteredTaxDetails.length]);
    };

    const setNewComponentRight = () => {
        const currentIndex = filteredTaxDetails.indexOf(selectedTaxDetails) || 0;
        setSelectedTaxDetails(filteredTaxDetails[(currentIndex + 1) % filteredTaxDetails.length]);
    };

    const handleCloseViewModal = () => setIsViewOpen(false);

    const handleOpenCreateModal = () => setIsCreateOpen(true);

    return (
        <div className="mx-auto pl-4 pr-4 h-screen w-full">
            
            {/* Top Controls */}
            <ListHeader
                title='Tax Detail List'
                searchQuery={searchQuery}
                handleSearchChange={(e) => setSearchQuery(e.target.value)}
                handleSearch={handleSearch}
                viewMode={viewMode}
                handleViewModeChange={setViewMode}
                handleOpenCreateModal={handleOpenCreateModal}
            />




            <div className="flex justify-center items-center">
                <ColorRing
                    visible={isLoading}
                    height="200"
                    width="200"
                    ariaLabel="color-ring-loading"
                    colors={['#033B5C', '#033B5C', '#033B5C', '#033B5C', '#033B5C']}
                />
            </div>

            {
                !isLoading && (
                    <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
                        <thead className="bg-gray-200">
                            <tr>
                                {TaxFields.map((field) => (
                                    <th
                                        key={field.key}
                                        className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                                    >
                                        {field.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-gray-700">
                            {filteredTaxDetails.map((tax) => (
                                <tr key={tax.id} className="border-t">
                                    {TaxFields.map((field) => (
                                        <td key={field.key} className="px-4 py-2">
                                            {field.key === 'actions' ? (
                                                <button className="text-sss-info"
                                                    onClick={() => handleOpenViewModal(tax)}
                                                >View</button>
                                            ) : (
                                                tax[field.key]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            }


            <ViewTaxDetailsModal

                open={isViewOpen}
                handleClose={handleCloseViewModal}
                taxDetail={selectedTaxDetails}
                setTaxDetail={setSelectedTaxDetails}
                setNext={setNewComponentRight}
                setPrev={setNewComponentLeft}

                allTaxDetails={taxDetails}
                updateAll={setTaxDetails}

            />

            <AddTaxDetailsModal
                open={isCreateOpen}
                handleClose={() => setIsCreateOpen(false)}
            />

            <div className="fixed bottom-0 left-0 w-full flex justify-between items-center p-4 border-t border-gray-300 bg-white">


                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, value) => setCurrentPage(value)}
                    color="primary"
                    shape="rounded"

                />

                <div className="flex items-center">
                    <label htmlFor="rows-per-page" className="mr-2">
                        Rows per page:
                    </label>
                    <select
                        id="rows-per-page"
                        onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                        value={rowsPerPage} // Assuming `pageSize` holds the current rows-per-page value
                        className="border border-gray-300 rounded p-2"
                    >

                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>

                    </select>
                </div>


            </div>



        </div >
    );
};

export default TaxDetailsList;